import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Download,
  FileText,
  Package,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useApprovePurchaseOrder,
  useCancelPurchaseOrder,
  useDeletePurchaseOrder,
  usePOReceipts,
  usePurchaseOrder,
  usePurchaseOrderPdfUrl,
  useReceivePurchaseOrder,
  useRejectPurchaseOrder,
  useSendPurchaseOrder,
  useSubmitPurchaseOrder,
  useUpdatePurchaseOrder,
  useUpdatePurchaseOrderLine,
} from '@/hooks/use-procurement';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { formatMoney } from '@/lib/currency';
import { formErrorText } from '@/lib/form-errors';
import type {
  POApprovalRole,
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderStatus,
  ReceiptLineCondition,
  ReceiveLinePayload,
} from '@/lib/types';

const statusColors: Record<PurchaseOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  sent: 'bg-indigo-100 text-indigo-800',
  partially_received: 'bg-purple-100 text-purple-800',
  received: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sent: 'Sent',
  partially_received: 'Partially Received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const roleLabels: Record<POApprovalRole, string> = {
  manager: 'Manager',
  admin: 'Admin',
  client: 'Client',
};

const FIELD_LABELS: Record<string, string> = {
  po_number: 'PO number',
  currency: 'Currency',
  expected_delivery: 'Expected delivery',
  payment_terms: 'Payment terms',
  notes: 'Notes',
  delivery_note_number: 'Delivery note number',
  lines: 'Line',
  quantity_received: 'Quantity received',
  serial_numbers: 'Serial numbers',
  role: 'Approval role',
};

function errorMessage(
  err: unknown,
  fallback: string,
  rowLabel?: (listField: string, index: number) => string | undefined
): string {
  return formErrorText(err, fallback, FIELD_LABELS, rowLabel);
}

export function PurchaseOrderDetailPage() {
  const timeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const { id } = useParams<{ id: string }>();
  const poId = Number(id);
  const navigate = useNavigate();
  const { data: po, isLoading, isError } = usePurchaseOrder(poId);
  const pdfUrl = usePurchaseOrderPdfUrl(poId);

  const submitMut = useSubmitPurchaseOrder();
  const approveMut = useApprovePurchaseOrder();
  const rejectMut = useRejectPurchaseOrder();
  const sendMut = useSendPurchaseOrder();
  const cancelMut = useCancelPurchaseOrder();
  const deleteMut = useDeletePurchaseOrder();
  const updateMut = useUpdatePurchaseOrder();

  const receiveMut = useReceivePurchaseOrder();
  const { data: receiptsData } = usePOReceipts(poId);
  const [showReceiveForm, setShowReceiveForm] = useState(false);

  const [approveRole, setApproveRole] = useState<POApprovalRole>('manager');
  const [approveNotes, setApproveNotes] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const actionErrorRef = useRef<HTMLDivElement>(null);

  // Edit Draft, Receive and the approval controls sit well below the banner.
  useEffect(() => {
    const banner = actionErrorRef.current;
    if (actionError && banner && typeof banner.scrollIntoView === 'function') {
      banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [actionError]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (isError || !po) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Failed to load purchase order.</p>
        <Button asChild className="mt-4" variant="outline">
          <Link to="/purchase-orders">Back to list</Link>
        </Button>
      </div>
    );
  }

  const canEdit = po.status === 'draft';
  const canSubmit = po.status === 'draft' && po.lines.length > 0;
  const canApprove = po.status === 'pending_approval';
  const canSend = po.status === 'approved';
  const canReceive = po.status === 'sent' || po.status === 'partially_received';
  const canCancel = !['received', 'closed', 'cancelled'].includes(po.status);
  const canDelete = po.status === 'draft';

  const outstanding = po.outstanding_roles ?? [];
  const selectedRole: POApprovalRole = outstanding.includes(approveRole)
    ? approveRole
    : (outstanding[0] ?? approveRole);

  const runAction = async (fn: () => Promise<unknown>, fallback: string) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(errorMessage(err, fallback));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/purchase-orders">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 font-mono">
                {po.po_number}
              </h1>
              <Badge className={statusColors[po.status]}>
                {statusLabels[po.status]}
              </Badge>
            </div>
            <p className="text-gray-600 text-sm mt-1">
              Vendor: <span className="font-medium">{po.vendor_name}</span>
              {po.created_by_username && (
                <>
                  {' '}· Created by{' '}
                  <span className="font-medium">{po.created_by_username}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-1" />
              PDF
            </a>
          </Button>
          {canSubmit && (
            <Button
              onClick={() =>
                runAction(
                  () => submitMut.mutateAsync(po.id),
                  'Failed to submit for approval'
                )
              }
              disabled={submitMut.isPending}
            >
              Submit for Approval
            </Button>
          )}
          {canSend && (
            <Button
              onClick={() =>
                runAction(() => sendMut.mutateAsync(po.id), 'Failed to send PO')
              }
              disabled={sendMut.isPending}
            >
              <Send className="h-4 w-4 mr-1" />
              Mark as Sent
            </Button>
          )}
          {canReceive && (
            <Button
              onClick={() => setShowReceiveForm(!showReceiveForm)}
              variant={showReceiveForm ? 'outline' : 'default'}
            >
              <Package className="h-4 w-4 mr-1" />
              {showReceiveForm ? 'Cancel Receive' : 'Receive'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              onClick={() =>
                runAction(
                  () => cancelMut.mutateAsync(po.id),
                  'Failed to cancel PO'
                )
              }
              disabled={cancelMut.isPending}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={async () => {
                if (!window.confirm('Delete this draft purchase order?')) return;
                await runAction(async () => {
                  await deleteMut.mutateAsync(po.id);
                  navigate('/purchase-orders');
                }, 'Failed to delete PO');
              }}
              disabled={deleteMut.isPending}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div
          ref={actionErrorRef}
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {actionError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Line Items
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {po.lines.length === 0 ? (
              <div className="p-6 text-sm text-gray-500 text-center">
                No line items yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit</TableHead>
                    <TableHead className="text-right">Tax %</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {po.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="text-gray-500">
                        {line.line_number}
                      </TableCell>
                      <TableCell>{line.description}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">
                        {line.sku || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {Number(line.quantity).toString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMoney(line.unit_cost, po.currency || orgCurrency)}
                      </TableCell>
                      <TableCell className="text-right">
                        {Number(line.tax_rate).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(line.line_total, po.currency || orgCurrency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>{formatMoney(po.subtotal, po.currency || orgCurrency)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Tax</span>
              <span>{formatMoney(po.tax, po.currency || orgCurrency)}</span>
            </div>
            <div className="flex justify-between font-semibold text-gray-900 border-t pt-2">
              <span>Total</span>
              <span>{formatMoney(po.total, po.currency || orgCurrency)}</span>
            </div>
            <div className="border-t pt-2 text-gray-600 space-y-1">
              <div>
                <span className="text-gray-500">Currency:</span>{' '}
                <span className="font-medium">{po.currency}</span>
              </div>
              <div>
                <span className="text-gray-500">Expected Delivery:</span>{' '}
                <span className="font-medium">
                  {po.expected_delivery
                    ? formatDateOnly(po.expected_delivery)
                    : '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Payment Terms:</span>{' '}
                <span className="font-medium">{po.payment_terms || '—'}</span>
              </div>
              {po.sent_at && (
                <div>
                  <span className="text-gray-500">Sent:</span>{' '}
                  <span className="font-medium">
                    {formatDateTimeInTimeZone(po.sent_at, timeZone)}
                  </span>
                </div>
              )}
              {po.approved_at && (
                <div>
                  <span className="text-gray-500">Approved:</span>{' '}
                  <span className="font-medium">
                    {formatDateTimeInTimeZone(po.approved_at, timeZone)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Approval Chain</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {outstanding.length > 0 ? (
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="text-gray-600">Outstanding roles:</span>
              {outstanding.map((r) => (
                <Badge key={r} variant="outline">
                  {roleLabels[r]}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No outstanding approvals required for this amount.
            </p>
          )}

          {po.approvals.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead>Approver</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {po.approvals.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{roleLabels[a.role]}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          a.decision === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }
                      >
                        {a.decision}
                      </Badge>
                    </TableCell>
                    <TableCell>{a.approver_username || '—'}</TableCell>
                    <TableCell className="text-gray-600">
                      {a.decided_at
                        ? formatDateTimeInTimeZone(a.decided_at, timeZone)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {a.notes || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {canApprove && (
            <div className="rounded-md border bg-gray-50 p-4 space-y-3">
              <p className="text-sm font-medium text-gray-900">
                Record approval decision
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Acting as role</Label>
                  <select
                    value={approveRole}
                    onChange={(e) =>
                      setApproveRole(e.target.value as POApprovalRole)
                    }
                    className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                  >
                    {(outstanding.length > 0
                      ? outstanding
                      : (['manager', 'admin', 'client'] as POApprovalRole[])
                    ).map((r) => (
                      <option key={r} value={r}>
                        {roleLabels[r]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={approveNotes}
                    onChange={(e) => setApproveNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() =>
                    runAction(
                      () =>
                          rejectMut.mutateAsync({
                            id: po.id,
                            role: selectedRole,
                            notes: approveNotes,
                          }),
                      'Failed to reject PO'
                    )
                  }
                  disabled={rejectMut.isPending}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button
                  onClick={() =>
                    runAction(
                      () =>
                          approveMut.mutateAsync({
                            id: po.id,
                            role: selectedRole,
                            notes: approveNotes,
                          }),
                      'Failed to approve PO'
                    )
                  }
                  disabled={approveMut.isPending}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Edit Draft</CardTitle>
          </CardHeader>
          <CardContent>
            <EditDraftForm
              po={po}
              saving={updateMut.isPending}
              onSave={async (patch) => {
                await runAction(
                  () => updateMut.mutateAsync({ id: po.id, data: patch }),
                  'Failed to save changes'
                );
              }}
            />
            {po.lines.length > 0 && <LineTaxForm po={po} />}
          </CardContent>
        </Card>
      )}

      {showReceiveForm && canReceive && (
        <ReceiveForm
          po={po}
          saving={receiveMut.isPending}
          onSubmit={async (payload) => {
            setActionError(null);
            try {
              await receiveMut.mutateAsync({ id: po.id, data: payload });
              setShowReceiveForm(false);
            } catch (err) {
              // Lines with nothing received are left out of the request, so
              // name a failing line by its PO line description.
              setActionError(
                errorMessage(err, 'Failed to receive goods', (listField, index) => {
                  const poLineId = payload.lines[index]?.po_line;
                  const line = po.lines.find((l) => l.id === poLineId);
                  return listField === 'lines' && line ? `Line "${line.description}"` : undefined;
                })
              );
            }
          }}
          onCancel={() => setShowReceiveForm(false)}
        />
      )}

      {receiptsData && receiptsData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Receipts ({receiptsData.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {receiptsData.map((r) => (
              <div
                key={r.id}
                className="border rounded-md p-3 text-sm space-y-1"
              >
                <div className="flex justify-between">
                  <span className="font-medium">
                    {r.delivery_note_number || `Receipt #${r.id}`}
                  </span>
                  <span className="text-gray-500">
                    {formatDateTimeInTimeZone(r.received_at, timeZone)}
                  </span>
                </div>
                {r.received_by_username && (
                  <div className="text-gray-600">
                    Received by: {r.received_by_username}
                  </div>
                )}
                {r.lines.map((rl) => (
                  <div key={rl.id} className="text-gray-600 pl-3">
                    Line #{rl.po_line}: {rl.quantity_received} received
                    ({rl.condition})
                    {rl.serial_numbers.length > 0 && (
                      <span className="text-xs ml-1">
                        SN: {rl.serial_numbers.join(', ')}
                      </span>
                    )}
                  </div>
                ))}
                {(r.quickbooks_bill_id || r.xero_bill_id) && (
                  <div className="text-xs text-green-700 mt-1">
                    {r.quickbooks_bill_id && `QB Bill: ${r.quickbooks_bill_id}`}
                    {r.quickbooks_bill_id && r.xero_bill_id && ' | '}
                    {r.xero_bill_id && `Xero Bill: ${r.xero_bill_id}`}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {po.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {po.notes}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LineTaxForm({ po }: { po: PurchaseOrder }) {
  const updateLine = useUpdatePurchaseOrderLine();
  const [rates, setRates] = useState<Record<number, string>>(() =>
    Object.fromEntries(po.lines.map((l) => [l.id, String(Number(l.tax_rate))]))
  );
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const errors: Record<number, string> = {};
    for (const line of po.lines) {
      const value = (rates[line.id] ?? '').trim();
      if (value === '') errors[line.id] = 'Tax % is required';
      else if (Number.isNaN(Number(value)) || Number(value) < 0)
        errors[line.id] = 'Tax % must be 0 or more';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const changed = po.lines.filter(
      (l) => Number(rates[l.id]) !== Number(l.tax_rate)
    );
    try {
      for (const line of changed) {
        await updateLine.mutateAsync({
          id: line.id,
          poId: po.id,
          data: { tax_rate: rates[line.id].trim() },
        });
      }
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Failed to save line tax'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-3 border-t pt-4" noValidate>
      <h3 className="text-sm font-semibold text-gray-900">Line tax</h3>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {saved && <p className="text-sm text-green-700">Line tax saved</p>}
      <div className="space-y-2">
        {po.lines.map((line) => (
          <div key={line.id} className="grid grid-cols-12 items-start gap-2">
            <Label
              htmlFor={`line-tax-${line.id}`}
              className="col-span-8 pt-2 text-sm font-normal text-gray-700"
            >
              {line.line_number}. {line.description}
            </Label>
            <div className="col-span-4">
              <Input
                id={`line-tax-${line.id}`}
                type="number"
                step="0.001"
                min="0"
                aria-label={`Tax % for line ${line.line_number}`}
                aria-invalid={fieldErrors[line.id] ? true : undefined}
                className={fieldErrors[line.id] ? 'border-red-500' : undefined}
                value={rates[line.id] ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setRates((prev) => ({ ...prev, [line.id]: value }));
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[line.id];
                    return next;
                  });
                }}
              />
              {fieldErrors[line.id] && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors[line.id]}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button type="submit" variant="outline" disabled={updateLine.isPending}>
          {updateLine.isPending ? 'Saving…' : 'Save line tax'}
        </Button>
      </div>
    </form>
  );
}

interface EditDraftFormProps {
  po: {
    po_number: string;
    currency: string;
    expected_delivery: string | null;
    payment_terms: string;
    notes: string;
  };
  saving: boolean;
  onSave: (patch: {
    po_number?: string;
    currency?: string;
    expected_delivery?: string | null;
    payment_terms?: string;
    notes?: string;
  }) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Receive Form (S18)
// ---------------------------------------------------------------------------

interface ReceiveLineState {
  po_line: number;
  description: string;
  ordered: number;
  already_received: number;
  quantity_received: string;
  condition: ReceiptLineCondition;
  serial_numbers: string;
  notes: string;
}

interface ReceiveFormProps {
  po: PurchaseOrder;
  saving: boolean;
  onSubmit: (payload: {
    lines: ReceiveLinePayload[];
    delivery_note_number?: string;
    notes?: string;
  }) => Promise<void>;
  onCancel: () => void;
}

function ReceiveForm({ po, saving, onSubmit, onCancel }: ReceiveFormProps) {
  const [deliveryNote, setDeliveryNote] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiveLineState[]>(() =>
    po.lines.map((l: PurchaseOrderLine) => ({
      po_line: l.id,
      description: l.description,
      ordered: Number(l.quantity),
      already_received: Number(l.received_qty),
      quantity_received: String(
        Math.max(0, Number(l.quantity) - Number(l.received_qty))
      ),
      condition: 'good' as ReceiptLineCondition,
      serial_numbers: '',
      notes: '',
    }))
  );

  const updateLine = (idx: number, patch: Partial<ReceiveLineState>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: ReceiveLinePayload[] = lines
      .filter((l) => Number(l.quantity_received) > 0)
      .map((l) => ({
        po_line: l.po_line,
        quantity_received: l.quantity_received,
        condition: l.condition,
        serial_numbers: l.serial_numbers
          ? l.serial_numbers.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        notes: l.notes,
      }));
    if (payload.length === 0) return;
    await onSubmit({
      lines: payload,
      delivery_note_number: deliveryNote,
      notes,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4" />
          Receive Goods
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Delivery Note #</Label>
              <Input
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="DN-001"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right w-20">Ordered</TableHead>
                <TableHead className="text-right w-20">Received</TableHead>
                <TableHead className="text-right w-24">Qty to Receive</TableHead>
                <TableHead className="w-28">Condition</TableHead>
                <TableHead>Serial Numbers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, idx) => (
                <TableRow key={line.po_line}>
                  <TableCell className="text-sm">{line.description}</TableCell>
                  <TableCell className="text-right">{line.ordered}</TableCell>
                  <TableCell className="text-right">
                    {line.already_received}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      max={line.ordered - line.already_received}
                      step="1"
                      value={line.quantity_received}
                      onChange={(e) =>
                        updateLine(idx, { quantity_received: e.target.value })
                      }
                      className="w-24 text-right"
                    />
                  </TableCell>
                  <TableCell>
                    <select
                      value={line.condition}
                      onChange={(e) =>
                        updateLine(idx, {
                          condition: e.target.value as ReceiptLineCondition,
                        })
                      }
                      className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                    >
                      <option value="good">Good</option>
                      <option value="damaged">Damaged</option>
                      <option value="short">Short</option>
                    </select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={line.serial_numbers}
                      onChange={(e) =>
                        updateLine(idx, { serial_numbers: e.target.value })
                      }
                      placeholder="SN1, SN2, ..."
                      className="text-xs"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || lines.every((l) => Number(l.quantity_received) <= 0)}
            >
              {saving ? 'Receiving...' : 'Confirm Receipt'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EditDraftForm({ po, saving, onSave }: EditDraftFormProps) {
  const [form, setForm] = useState({
    po_number: po.po_number,
    currency: po.currency,
    expected_delivery: po.expected_delivery ?? '',
    payment_terms: po.payment_terms,
    notes: po.notes,
  });

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        await onSave({
          po_number: form.po_number,
          currency: form.currency,
          expected_delivery: form.expected_delivery || null,
          payment_terms: form.payment_terms,
          notes: form.notes,
        });
      }}
      className="space-y-3"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>PO Number</Label>
          <Input
            value={form.po_number}
            onChange={(e) =>
              setForm((p) => ({ ...p, po_number: e.target.value }))
            }
          />
        </div>
        <div>
          <Label htmlFor="draft-currency">Currency</Label>
          <CurrencySelect
            id="draft-currency"
            value={form.currency}
            onValueChange={(value) =>
              setForm((p) => ({ ...p, currency: value }))
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            {CURRENCY_NO_CONVERSION_NOTE}
          </p>
        </div>
        <div>
          <Label>Expected Delivery</Label>
          <Input
            type="date"
            value={form.expected_delivery}
            onChange={(e) =>
              setForm((p) => ({ ...p, expected_delivery: e.target.value }))
            }
          />
        </div>
        <div>
          <Label>Payment Terms</Label>
          <Input
            value={form.payment_terms}
            onChange={(e) =>
              setForm((p) => ({ ...p, payment_terms: e.target.value }))
            }
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Notes</Label>
          <Textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
